import { el, on } from '../util/dom';
import { NetworkPolicy, CategoryFilter, DEFAULT_CATEGORIES, isValidDomain, normalizeDomain, getCategoryInfo } from './types';
import { getNetworkPolicy, updateNetworkPolicy, addBlockedDomain, removeBlockedDomain, addAllowedDomain, removeAllowedDomain, updateCategoryFilter, resetNetworkPolicy } from './queries';
import { NETWORK_DASHBOARD_STYLES } from './styles';

export interface NetworkDashboardProps {
  familyId: string;
  deviceId: string;
  onError?: (error: string) => void;
  onSuccess?: (message: string) => void;
}

export async function createNetworkDashboard(props: NetworkDashboardProps): Promise<HTMLElement> {
  const container = el('div', { class: 'network-dashboard' });

  // Add styles
  injectNetworkStyles();

  // Header
  const header = el('div', { class: 'network-header' });
  header.appendChild(el('h2', { class: 'network-title' }, 'Network Policy'));
  container.appendChild(header);

  // Loading state
  const loading = el('div', { class: 'network-loading' });
  loading.appendChild(el('div', { class: 'spinner' }, ''));
  loading.appendChild(el('div', {}, 'Loading network policy...'));
  container.appendChild(loading);

  try {
    // Load policy
    const policy = await getNetworkPolicy(props.familyId, props.deviceId);

    // Remove loading state
    loading.remove();

    if (!policy) {
      const error = el('div', { class: 'network-error' }, 'Failed to load network policy');
      container.appendChild(error);
      props.onError?.('Network policy not found');
      return container;
    }

    // Create sections
    const content = el('div', { class: 'network-content' });

    // VPN section
    content.appendChild(createVpnSection(props, policy));

    // DNS section
    content.appendChild(createDnsSection(props, policy));

    // SafeSearch section
    content.appendChild(createSafeSearchSection(props, policy));

    // Category filters section
    content.appendChild(createCategoryFiltersSection(props, policy));

    // Domain lists section
    content.appendChild(createDomainListsSection(props, policy));

    container.appendChild(content);
  } catch (err) {
    loading.remove();
    const errorDiv = el('div', { class: 'network-error' }, `Error loading network policy: ${(err as Error).message}`);
    container.appendChild(errorDiv);
    props.onError?.((err as Error).message);
  }

  return container;
}

// VPN section
function createVpnSection(props: NetworkDashboardProps, policy: NetworkPolicy): HTMLElement {
  const section = el('div', { class: 'network-section' });
  const title = el('h3', { class: 'section-title' }, '🔒 VPN');
  section.appendChild(title);

  const hint = el('div', { class: 'section-hint' }, 'VPN encrypts all device traffic and enables DNS filtering');
  section.appendChild(hint);

  const statusCard = el('div', { class: 'status-card' });
  const statusLabel = el('div', { class: 'status-label' }, policy.vpnEnabled ? 'VPN Enabled' : 'VPN Disabled');
  const statusIcon = el('div', {
    class: `status-icon ${policy.vpnEnabled ? 'enabled' : 'disabled'}`,
  }, policy.vpnEnabled ? '✓' : '✕');
  statusCard.appendChild(statusIcon);
  statusCard.appendChild(statusLabel);
  section.appendChild(statusCard);

  if (!policy.vpnPermissionGranted) {
    const warning = el('div', { class: 'warning-box' }, '⚠️ Device has not granted VPN permission');
    section.appendChild(warning);
  }

  return section;
}

// DNS filtering section
function createDnsSection(props: NetworkDashboardProps, policy: NetworkPolicy): HTMLElement {
  const section = el('div', { class: 'network-section' });
  const title = el('h3', { class: 'section-title' }, '🌐 DNS Filtering');
  section.appendChild(title);

  const hint = el('div', { class: 'section-hint' }, 'Routes all DNS requests through content filter');
  section.appendChild(hint);

  const toggleLabel = el('label', { class: 'toggle-label' });
  const toggle = el('input', {
    type: 'checkbox',
    class: 'toggle-input',
    checked: policy.dnsFilteringEnabled ? 'checked' : undefined,
  }) as HTMLInputElement;

  on(toggle, 'change', async () => {
    try {
      await updateNetworkPolicy(props.familyId, props.deviceId, {
        dnsFilteringEnabled: toggle.checked,
      });
      props.onSuccess?.('DNS filtering updated');
    } catch (err) {
      alert(`Failed to update DNS filtering: ${(err as Error).message}`);
      toggle.checked = !toggle.checked;
    }
  });

  toggleLabel.appendChild(toggle);
  toggleLabel.appendChild(el('span', {}, policy.dnsFilteringEnabled ? 'Enabled' : 'Disabled'));
  section.appendChild(toggleLabel);

  return section;
}

// SafeSearch section
function createSafeSearchSection(props: NetworkDashboardProps, policy: NetworkPolicy): HTMLElement {
  const section = el('div', { class: 'network-section' });
  const title = el('h3', { class: 'section-title' }, '🔍 SafeSearch');
  section.appendChild(title);

  const hint = el('div', { class: 'section-hint' }, 'Enables safe search on Google, Bing, YouTube');
  section.appendChild(hint);

  const toggleLabel = el('label', { class: 'toggle-label' });
  const toggle = el('input', {
    type: 'checkbox',
    class: 'toggle-input',
    checked: policy.safeSearchEnabled ? 'checked' : undefined,
  }) as HTMLInputElement;

  on(toggle, 'change', async () => {
    try {
      await updateNetworkPolicy(props.familyId, props.deviceId, {
        safeSearchEnabled: toggle.checked,
      });
      props.onSuccess?.('SafeSearch updated');
    } catch (err) {
      alert(`Failed to update SafeSearch: ${(err as Error).message}`);
      toggle.checked = !toggle.checked;
    }
  });

  toggleLabel.appendChild(toggle);
  toggleLabel.appendChild(el('span', {}, policy.safeSearchEnabled ? 'Enabled' : 'Disabled'));
  section.appendChild(toggleLabel);

  return section;
}

// Category filters section
function createCategoryFiltersSection(props: NetworkDashboardProps, policy: NetworkPolicy): HTMLElement {
  const section = el('div', { class: 'network-section' });
  const title = el('h3', { class: 'section-title' }, '📋 Content Categories');
  section.appendChild(title);

  const hint = el('div', { class: 'section-hint' }, 'Select content categories to block');
  section.appendChild(hint);

  const categories = policy.categoryFilters || DEFAULT_CATEGORIES;
  const grid = el('div', { class: 'categories-grid' });

  categories.forEach(cat => {
    const item = el('div', { class: `category-item ${cat.enabled ? 'enabled' : 'disabled'}` });
    const label = el('label', { class: 'category-label' });

    const checkbox = el('input', {
      type: 'checkbox',
      class: 'category-checkbox',
      checked: cat.enabled ? 'checked' : undefined,
    }) as HTMLInputElement;

    on(checkbox, 'change', async () => {
      try {
        await updateCategoryFilter(props.familyId, props.deviceId, cat.id, checkbox.checked);
        item.classList.toggle('enabled');
        props.onSuccess?.(`${cat.displayName} ${checkbox.checked ? 'enabled' : 'disabled'}`);
      } catch (err) {
        alert(`Failed to update category: ${(err as Error).message}`);
        checkbox.checked = !checkbox.checked;
      }
    });

    label.appendChild(checkbox);
    label.appendChild(el('span', {}, cat.displayName));
    item.appendChild(label);
    grid.appendChild(item);
  });

  section.appendChild(grid);

  return section;
}

// Domain lists section
function createDomainListsSection(props: NetworkDashboardProps, policy: NetworkPolicy): HTMLElement {
  const section = el('div', { class: 'network-section' });
  const title = el('h3', { class: 'section-title' }, '🚫 Custom Domain Rules');
  section.appendChild(title);

  const hint = el('div', { class: 'section-hint' }, 'Manually block or allow specific domains');
  section.appendChild(hint);

  // Input area
  const inputArea = el('div', { class: 'domain-input-area' });
  const input = el('input', {
    type: 'text',
    class: 'domain-input',
    placeholder: 'Enter domain (e.g., example.com)',
  }) as HTMLInputElement;

  const typeSelect = el('select', { class: 'domain-type-select' }) as HTMLSelectElement;
  typeSelect.appendChild(el('option', { value: 'block' }, 'Block'));
  typeSelect.appendChild(el('option', { value: 'allow' }, 'Allow'));

  const addBtn = el('button', { class: 'btn btn-primary btn-sm' }, 'Add');
  on(addBtn, 'click', async () => {
    const domain = normalizeDomain(input.value);
    if (!domain) {
      alert('Please enter a domain');
      return;
    }
    if (!isValidDomain(domain)) {
      alert('Invalid domain format');
      return;
    }

    try {
      addBtn.disabled = true;
      if (typeSelect.value === 'block') {
        await addBlockedDomain(props.familyId, props.deviceId, domain);
      } else {
        await addAllowedDomain(props.familyId, props.deviceId, domain);
      }
      input.value = '';
      props.onSuccess?.(`Domain added: ${domain}`);
    } catch (err) {
      alert(`Failed to add domain: ${(err as Error).message}`);
      addBtn.disabled = false;
    }
  });

  inputArea.appendChild(input);
  inputArea.appendChild(typeSelect);
  inputArea.appendChild(addBtn);
  section.appendChild(inputArea);

  // Lists
  if (policy.blockedDomains && policy.blockedDomains.length > 0) {
    const blockedSection = el('div', { class: 'domain-list' });
    blockedSection.appendChild(el('h4', { class: 'domain-list-title' }, '🔴 Blocked Domains'));

    const list = el('div', { class: 'domain-items' });
    policy.blockedDomains.forEach(domain => {
      const item = el('div', { class: 'domain-item' });
      item.appendChild(el('span', { class: 'domain-name' }, domain));

      const deleteBtn = el('button', { class: 'btn btn-sm btn-danger' }, '×');
      on(deleteBtn, 'click', async () => {
        try {
          await removeBlockedDomain(props.familyId, props.deviceId, domain);
          item.remove();
          props.onSuccess?.(`Domain removed: ${domain}`);
        } catch (err) {
          alert(`Failed to remove domain: ${(err as Error).message}`);
        }
      });
      item.appendChild(deleteBtn);
      list.appendChild(item);
    });

    blockedSection.appendChild(list);
    section.appendChild(blockedSection);
  }

  if (policy.allowedDomains && policy.allowedDomains.length > 0) {
    const allowedSection = el('div', { class: 'domain-list' });
    allowedSection.appendChild(el('h4', { class: 'domain-list-title' }, '🟢 Allowed Domains'));

    const list = el('div', { class: 'domain-items' });
    policy.allowedDomains.forEach(domain => {
      const item = el('div', { class: 'domain-item' });
      item.appendChild(el('span', { class: 'domain-name' }, domain));

      const deleteBtn = el('button', { class: 'btn btn-sm btn-danger' }, '×');
      on(deleteBtn, 'click', async () => {
        try {
          await removeAllowedDomain(props.familyId, props.deviceId, domain);
          item.remove();
          props.onSuccess?.(`Domain removed: ${domain}`);
        } catch (err) {
          alert(`Failed to remove domain: ${(err as Error).message}`);
        }
      });
      item.appendChild(deleteBtn);
      list.appendChild(item);
    });

    allowedSection.appendChild(list);
    section.appendChild(allowedSection);
  }

  return section;
}

// Helper: Inject styles
function injectNetworkStyles() {
  if (document.getElementById('network-dashboard-styles')) return;

  const style = el('style');
  style.id = 'network-dashboard-styles';
  style.textContent = NETWORK_DASHBOARD_STYLES;
  document.head.appendChild(style);
}
